import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { workspaceExtras } from '@/lib/workspace-extras';
import { feedbackApi } from '@/lib/feedback';
import { AppShell } from '@/components/app/AppShell';
import { JpDate } from '@/components/jp/JpDate';
import { JpNumber } from '@/components/jp/JpNumber';

export function ReportsPage() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const ws = (p: string) => `/app/workspace/${wsId}/${p}`;
  const { data, isLoading } = useQuery({
    queryKey: ['reports', wsId],
    queryFn: () => workspaceExtras.reports(wsId!),
    enabled: !!wsId,
    refetchInterval: 60_000,
  });
  const { data: feedback } = useQuery({
    queryKey: ['feedback-summary', wsId],
    queryFn: () => feedbackApi.summary(wsId!),
    enabled: !!wsId,
    refetchInterval: 60_000,
  });
  const { data: quality } = useQuery({
    queryKey: ['answer-quality', wsId],
    queryFn: () => workspaceExtras.quality(wsId!),
    enabled: !!wsId,
    refetchInterval: 60_000,
  });
  const pct = (r: number | null) => (r == null ? '—' : `${Math.round(r * 100)}%`);

  return (
    <AppShell
      title={t('reports.title', { defaultValue: 'Reports' })}
      description={t('reports.subtitle', { defaultValue: 'Top queries, AI cost trend, and ingestion health for this workspace.' }) as string}
      maxWidth="max-w-5xl"
    >
      {isLoading || !data ? (
        <div className="card-paper p-10 text-center text-ink-500 text-sm">{t('common.loading')}</div>
      ) : (
        <div className="space-y-6">
          {quality && (
            <section>
              <h3 className="text-[11px] uppercase tracking-[0.16em] text-ink-500 font-semibold mb-1">
                {t('reports.quality', { defaultValue: 'Answer quality' })}
              </h3>
              <p className="text-[12px] text-ink-500 mb-3">
                {t('reports.quality_window', {
                  defaultValue: 'We measure accuracy — last {{n}} days · {{total}} answers',
                  n: quality.windowDays,
                  total: quality.totalAnswers,
                })}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="card-paper p-4">
                  <div className="text-[11px] uppercase tracking-[0.12em] text-ink-500 font-semibold mb-1">{t('reports.quality_grounded', { defaultValue: 'Cited answers' })}</div>
                  <div className="num text-2xl font-semibold gradient-text">{pct(quality.groundedRate)}</div>
                  <div className="text-[11px] text-ink-400 mt-0.5">{t('reports.quality_grounded_hint', { defaultValue: 'cite a source' })}</div>
                </div>
                <div className="card-paper p-4">
                  <div className="text-[11px] uppercase tracking-[0.12em] text-ink-500 font-semibold mb-1">{t('reports.quality_no_evidence', { defaultValue: 'No evidence' })}</div>
                  <div className={`num text-2xl font-semibold ${(quality.noEvidenceRate ?? 0) > 0.2 ? 'text-warn-600' : 'text-ink-900'}`}>{pct(quality.noEvidenceRate)}</div>
                  <div className="text-[11px] text-ink-400 mt-0.5">{t('reports.quality_no_evidence_hint', { defaultValue: 'corpus had no match' })}</div>
                </div>
                <div className="card-paper p-4">
                  <div className="text-[11px] uppercase tracking-[0.12em] text-ink-500 font-semibold mb-1">{t('reports.quality_avg_citations', { defaultValue: 'Avg citations' })}</div>
                  <div className="num text-2xl font-semibold text-ink-900">{quality.avgCitations.toFixed(1)}</div>
                  <div className="text-[11px] text-ink-400 mt-0.5">{t('reports.quality_per_answer', { defaultValue: 'per answer' })}</div>
                </div>
                <div className="card-paper p-4">
                  <div className="text-[11px] uppercase tracking-[0.12em] text-ink-500 font-semibold mb-1">{t('reports.quality_helpful', { defaultValue: 'Rated helpful' })}</div>
                  <div className="num text-2xl font-semibold text-ink-900">{pct(quality.feedback.helpfulRate)}</div>
                  <div className="text-[11px] text-ink-400 mt-0.5">{quality.feedback.helpful}👍 · {quality.feedback.unhelpful}👎</div>
                </div>
              </div>
            </section>
          )}

          <section>
            <h3 className="text-[11px] uppercase tracking-[0.16em] text-ink-500 font-semibold mb-3">
              {t('reports.ingest', { defaultValue: 'Ingestion' })}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Stat label={t('reports.docs') as string} value={data.ingestion.documents.total} to={ws('docs')} />
              <Stat label={t('reports.indexed') as string} value={data.ingestion.documents.indexed} accent to={ws('docs?status=indexed')} />
              <Stat label={t('reports.failed') as string} value={data.ingestion.documents.failed} danger={data.ingestion.documents.failed > 0} to={ws('docs?status=failed')} />
              <Stat label={t('reports.pending') as string} value={data.ingestion.documents.pending} to={ws('docs?status=pending')} />
              <Stat label={t('reports.chunks') as string} value={data.ingestion.chunks} accent />
            </div>
          </section>

          <section>
            <h3 className="text-[11px] uppercase tracking-[0.16em] text-ink-500 font-semibold mb-3">
              {t('reports.ai', { defaultValue: 'AI cost (last 30 days)' })}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="card-paper p-5 md:col-span-2">
                <div className="text-[11px] uppercase tracking-[0.12em] text-ink-500 font-semibold mb-2">
                  {t('reports.ai_7d', { defaultValue: 'Last 7 days' })}
                </div>
                <Last7Days data={data.ai.last7Days} />
              </div>
              <div className="card-paper p-5">
                <div className="text-[11px] uppercase tracking-[0.12em] text-ink-500 font-semibold mb-2">
                  {t('reports.ai_summary', { defaultValue: '30-day summary' })}
                </div>
                <div className="num text-3xl font-semibold text-ink-900">
                  ${(data.ai.totalCents30d / 100).toFixed(2)}
                </div>
                <div className="text-[12px] text-ink-500 num mt-1">
                  <JpNumber value={data.ai.runs30d} /> {t('reports.runs', { defaultValue: 'runs' })}
                </div>
                {data.ai.byModel.length > 0 && (
                  <ul className="mt-4 space-y-1.5 text-[12px]">
                    {data.ai.byModel.slice(0, 6).map((m) => (
                      <li key={m.model} className="flex justify-between gap-2">
                        <span className="num text-ink-700 truncate">{m.model}</span>
                        <span className="num text-ink-900 font-semibold">${(m.cents / 100).toFixed(2)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-[11px] uppercase tracking-[0.16em] text-ink-500 font-semibold mb-3">
              {t('reports.relevance', { defaultValue: 'Search relevance (last 30 days)' })}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="card-paper p-5">
                <div className="text-[11px] uppercase tracking-[0.12em] text-ink-500 font-semibold mb-1.5">
                  {t('reports.helpful_rate', { defaultValue: 'Helpful rate' })}
                </div>
                <div className="num text-3xl font-semibold gradient-text">
                  {feedback && feedback.helpfulRate != null ? `${Math.round(feedback.helpfulRate * 100)}%` : '—'}
                </div>
                <div className="text-[12px] text-ink-500 num mt-1">
                  {feedback ? `${feedback.helpful} 👍 · ${feedback.unhelpful} 👎 · ${feedback.total} ${t('reports.votes', { defaultValue: 'votes' })}` : t('common.loading', { defaultValue: 'Loading…' })}
                </div>
              </div>
              <div className="card-paper p-5 md:col-span-2">
                <div className="text-[11px] uppercase tracking-[0.12em] text-ink-500 font-semibold mb-2">
                  {t('reports.recent_negatives', { defaultValue: 'Recent “not helpful”' })}
                </div>
                {feedback && feedback.recentNegatives.length > 0 ? (
                  <ul className="space-y-1.5 text-[12.5px]">
                    {feedback.recentNegatives.map((n, i) => (
                      <li key={i} className="flex items-start justify-between gap-3">
                        <span className="text-ink-800 truncate">
                          <span className="chip-citation text-[10px] uppercase mr-1.5">{n.kind}</span>
                          {n.query || t('reports.no_query', { defaultValue: '(no query captured)' })}
                          {n.note && <span className="text-ink-500"> — {n.note}</span>}
                        </span>
                        <span className="num text-[11px] text-ink-400 shrink-0"><JpDate value={n.createdAt} /></span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-[13px] text-ink-500">
                    {t('reports.no_negatives', { defaultValue: 'No negative feedback yet — rate answers with 👍/👎 on Ask and Search to build a relevance trend.' })}
                  </p>
                )}
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-[11px] uppercase tracking-[0.16em] text-ink-500 font-semibold mb-3">
              {t('reports.top_q', { defaultValue: 'Top questions (last 30 days)' })}
            </h3>
            {data.topQueries.length === 0 ? (
              <div className="card-paper p-8 text-center text-ink-500 text-sm">
                {t('reports.top_q_empty', { defaultValue: 'No questions yet.' })}{' '}
                <Link to={ws('ask')} className="text-accent-600 hover:underline">
                  {t('reports.ask_first', { defaultValue: 'Ask your first question →' })}
                </Link>
              </div>
            ) : (
              <div className="card-paper overflow-hidden">
                <ul className="divide-y divide-ink-100">
                  {data.topQueries.map((q, i) => (
                    <li key={i}>
                      <Link
                        to={ws(`ask?seed=${encodeURIComponent(q.question)}`)}
                        className="flex items-center justify-between gap-4 px-5 py-3 hover:bg-paper-100/40"
                        title={t('reports.rerun_q', { defaultValue: 'Re-ask this question' }) as string}
                      >
                        <span className="text-[13.5px] text-ink-800 truncate hover:underline">{q.question}</span>
                        <span className="num text-[12px] text-ink-500 shrink-0">{q.count}×</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          {data.connectors.length > 0 && (
            <section>
              <h3 className="text-[11px] uppercase tracking-[0.16em] text-ink-500 font-semibold mb-3">
                {t('reports.connectors', { defaultValue: 'Connectors' })}
              </h3>
              <div className="card-paper overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-ink-500 border-b border-ink-100 bg-paper-100 text-[11px] uppercase tracking-[0.12em]">
                      <th className="py-2.5 pl-5 pr-3 font-medium">{t('reports.connector')}</th>
                      <th className="py-2.5 px-3 font-medium">{t('reports.status')}</th>
                      <th className="py-2.5 px-3 font-medium text-right">{t('reports.items', { defaultValue: 'Items' })}</th>
                      <th className="py-2.5 px-3 font-medium">{t('reports.mode', { defaultValue: 'Mode' })}</th>
                      <th className="py-2.5 px-3 pr-5 font-medium">{t('reports.last_sync')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.connectors.map((c, i) => (
                      <tr key={i} className="border-b border-ink-50 hover:bg-paper-100/40">
                        <td className="py-2.5 pl-5 pr-3 capitalize">
                          <Link to={ws('connectors')} className="text-ink-900 hover:text-accent-600 hover:underline">{c.kind}</Link>
                          {c.error && <div className="text-[10.5px] text-warn-600 truncate max-w-[200px]" title={c.error}>{c.error}</div>}
                        </td>
                        <td className="py-2.5 px-3">
                          <span className={`chip-citation text-[10px] capitalize ${c.status === 'error' ? 'bg-warn-500/10 text-warn-600 border-warn-500/30' : ''}`}>
                            {c.status}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-right num text-[11.5px] text-ink-700">
                          {c.itemCount != null ? c.itemCount : '—'}
                        </td>
                        <td className="py-2.5 px-3">
                          <span className={`text-[10.5px] ${c.lastMode === 'incremental' ? 'text-emerald-600' : 'text-ink-500'}`}>
                            {c.lastMode === 'incremental' ? t('reports.delta', { defaultValue: 'delta' }) : t('reports.full', { defaultValue: 'full' })}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 pr-5 num text-[11.5px] text-ink-500">
                          {c.lastSync ? <JpDate value={c.lastSync} /> : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>
      )}
    </AppShell>
  );
}

function Stat({ label, value, accent, danger, to }: { label: string; value: number; accent?: boolean; danger?: boolean; to?: string }) {
  const inner = (
    <>
      <div className="text-[11px] uppercase tracking-[0.12em] text-ink-500 font-semibold mb-1.5">{label}</div>
      <div className={`num text-2xl font-semibold tabular-nums ${danger ? 'text-warn-600' : accent ? 'gradient-text' : 'text-ink-900'}`}>
        <JpNumber value={value} />
      </div>
    </>
  );
  if (to) {
    return <Link to={to} className="card-paper p-4 block hover:shadow-card hover:-translate-y-0.5 transition-all">{inner}</Link>;
  }
  return <div className="card-paper p-4">{inner}</div>;
}

function Last7Days({ data }: { data: { date: string; cents: number; runs: number }[] }) {
  const { t } = useTranslation();
  if (data.length === 0) {
    return <div className="text-[12px] text-ink-500">{t('reports.no_runs_7d', { defaultValue: 'No runs in the last 7 days.' })}</div>;
  }
  const max = Math.max(...data.map((d) => d.cents), 1);
  return (
    <div className="flex items-end gap-1.5 h-32">
      {data.map((d) => (
        <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group">
          <div
            className="w-full rounded-t bg-accent-500/80 group-hover:bg-accent-600 transition-colors min-h-[2px]"
            style={{ height: `${Math.max(2, (d.cents / max) * 100)}%` }}
            title={`${d.date}: $${(d.cents / 100).toFixed(2)} · ${d.runs} runs`}
          />
          <span className="num text-[10px] text-ink-500">{d.date.slice(5)}</span>
        </div>
      ))}
    </div>
  );
}
